import { Button } from "@ui/Button";
import { Dropdown, type DropdownOption } from "@ui/Dropdown";
import { InputContainer } from "@ui/InputContainer";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Search } from "@ui/Search";
import { HStack } from "@ui/Stack";
import { useAriaGrid } from "@utils/useAriaGrid";
import { createMemo, createSignal, createUniqueId, For } from "solid-js";

export const GROUP_ICON_OPTIONS = [
	"account_tree",
	"album",
	"analytics",
	"animation",
	"apartment",
	"auto_awesome",
	"badge",
	"bolt",
	"book_2",
	"bookmark",
	"brush",
	"cake",
	"campaign",
	"camping",
	"carpenter",
	"casino",
	"category",
	"celebration",
	"chair",
	"chat",
	"check_circle",
	"chess",
	"code",
	"computer",
	"construction",
	"dashboard",
	"dashboard_customize",
	"design_services",
	"directions_car",
	"directions_run",
	"diversity_3",
	"downhill_skiing",
	"eco",
	"equalizer",
	"explore",
	"extension",
	"favorite",
	"fitness_center",
	"flag",
	"flare",
	"flight",
	"folder",
	"forest",
	"forum",
	"graphic_eq",
	"grid_view",
	"group",
	"groups",
	"handyman",
	"hiking",
	"history_edu",
	"home",
	"home_repair_service",
	"hub",
	"inventory_2",
	"kitchen",
	"landscape",
	"library_music",
	"lightbulb",
	"local_cafe",
	"local_fire_department",
	"local_shipping",
	"luggage",
	"map",
	"memory",
	"menu_book",
	"mic",
	"monitor_heart",
	"movie",
	"movie_filter",
	"music_note",
	"nights_stay",
	"notes",
	"nutrition",
	"palette",
	"payments",
	"pedal_bike",
	"person",
	"person_celebrate",
	"pets",
	"photo_camera",
	"piano",
	"pool",
	"psychology",
	"public",
	"quiz",
	"radio",
	"restaurant",
	"rocket_launch",
	"route",
	"sailing",
	"schedule",
	"school",
	"science",
	"search_insights",
	"security",
	"self_improvement",
	"shield",
	"smart_display",
	"space_dashboard",
	"sports_bar",
	"sports_esports",
	"sports_martial_arts",
	"sports_score",
	"sports_soccer",
	"stacks",
	"star",
	"storefront",
	"style",
	"subtitles",
	"summarize",
	"support_agent",
	"surfing",
	"terrain",
	"toys",
	"train",
	"travel_explore",
	"trending_up",
	"two_wheeler",
	"verified_user",
	"video_library",
	"visibility",
	"volunteer_activism",
	"wand_stars",
	"work",
	"workspace_premium",
	"yard",
] as const;

const COLUMN_COUNT = 6;

const iconLabel = (icon: string) =>
	icon
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");

const ICON_DROPDOWN_OPTIONS: DropdownOption[] = [
	{ value: "", label: "No icon" },
	...GROUP_ICON_OPTIONS.map((icon) => ({
		value: icon,
		label: iconLabel(icon),
		symbol: icon,
	})),
];

type Props = {
	value: string;
	onChange: (icon: string) => void;
};

type GridProps = {
	value: string;
	onChange: (icon: string) => void;
};

const IconPickerGrid = (props: GridProps) => {
	const [search, setSearch] = createSignal("");
	const visibleIcons = createMemo(() => {
		const query = search().trim().toLowerCase().replace(/\s+/g, "_");
		return GROUP_ICON_OPTIONS.filter((icon) => icon.includes(query));
	});
	const rows = createMemo(() => {
		const icons = visibleIcons();
		const nextRows = [];
		for (let index = 0; index < icons.length; index += COLUMN_COUNT) {
			nextRows.push(icons.slice(index, index + COLUMN_COUNT));
		}
		return nextRows;
	});
	const selectedIndex = createMemo(() => {
		if (props.value === "") {
			return 0;
		}
		const index = visibleIcons().indexOf(
			props.value as (typeof GROUP_ICON_OPTIONS)[number],
		);
		return index >= 0 ? index : 0;
	});
	const grid = useAriaGrid({
		columnCount: COLUMN_COUNT,
		itemCount: () => visibleIcons().length,
		selectedIndex,
		onActivate: (index) => {
			const icon = visibleIcons()[index];
			if (icon) {
				props.onChange(icon);
			}
		},
	});

	return (
		<>
			<HStack align="center" spacing="small" fullWidth>
				<Search
					onClear={() => setSearch("")}
					value={search()}
					onSearch={(value) => setSearch(value)}
					placeholder="Search icons, e.g. music"
					style={{ flex: "1", "min-width": "0" }}
				/>
				<Button
					type="button"
					variant="text"
					theme="monochrome"
					size="small"
					onPointerDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
						if (props.value !== "") {
							props.onChange("");
						}
					}}
					onClick={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
					disabled={props.value === ""}
				>
					Clear
				</Button>
			</HStack>
			{/* biome-ignore lint/a11y/useSemanticElements: APG interactive icon picker uses aria grid with roving focus, not a data table. */}
			<div
				role="grid"
				{...grid.gridProps()}
				aria-label="Icons"
				style={{
					display: "grid",
					gap: "0.5rem",
					"margin-top": "0.75rem",
					"max-height": "18rem",
					"overflow-y": "auto",
					"padding-right": "0.25rem",
				}}
			>
				<For each={rows()}>
					{(row, rowIndex) => (
						<div
							{...grid.rowProps(rowIndex())}
							style={{
								display: "grid",
								"grid-template-columns": `repeat(${COLUMN_COUNT}, minmax(0, 1fr))`,
								gap: "0.5rem",
								padding: "0.25rem",
							}}
						>
							<For each={row}>
								{(icon, columnIndex) => {
									const index = () => rowIndex() * COLUMN_COUNT + columnIndex();
									const selected = () => props.value === icon;
									return (
										<Button
											{...grid.cellProps(index())}
											type="button"
											variant={selected() ? "primary" : "text"}
											theme={selected() ? "black" : "monochrome"}
											onPointerDown={(event) => {
												event.preventDefault();
												event.stopPropagation();
												props.onChange(icon);
											}}
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();
											}}
											aria-label={icon.replaceAll("_", " ")}
											aria-pressed={selected()}
											style={{
												"justify-content": "center",
												"min-width": 0,
											}}
										>
											<MaterialSymbol
												symbol={icon}
												relativeSize={1.5}
												filled={selected()}
											/>
										</Button>
									);
								}}
							</For>
						</div>
					)}
				</For>
			</div>
		</>
	);
};

export const IconPicker = (props: Props) => {
	const dropdownId = createUniqueId();
	let closePanel = () => {};
	const grid = (
		<IconPickerGrid
			value={props.value}
			onChange={(icon) => {
				props.onChange(icon);
				// Close after the gesture so the leftover click cannot retarget
				// onto the trigger and reopen the panel.
				window.setTimeout(() => closePanel(), 0);
			}}
		/>
	);

	return (
		<InputContainer>
			<label for={`${dropdownId}-button`}>
				Icon <span class="optional">(optional)</span>
			</label>
			<Dropdown
				id={dropdownId}
				value={props.value}
				placeholder="No icon"
				options={ICON_DROPDOWN_OPTIONS}
				listBoxStyle={{
					width: "min(22rem, calc(100vw - 2rem))",
				}}
				content={(api) => {
					closePanel = api.close;
					return grid;
				}}
			/>
		</InputContainer>
	);
};
